import {booleanAttribute, ChangeDetectionStrategy, Component, computed, HostBinding, input, output} from '@angular/core';
import {CommonModule} from '@angular/common';

export type V2ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline-primary'
  | 'outline-secondary'
  | 'outline-danger'
  | 'blue'
  | 'white';

export type V2ButtonSize = 'sm' | 'md';

/**
 * Design 2.0 button primitive — single source of truth for padding, radius,
 * typography, and palette. Swap the variant to change the color pair; every
 * other property stays visually aligned.
 *
 * Variants per Figma `AB/button`:
 *  - `primary`           — solid brand green (#149A58) / white text. Main CTAs.
 *  - `secondary`         — slate100 / slate800. Neutral actions.
 *  - `outline-primary`   — white bg + green border/text. Secondary CTA accents.
 *  - `outline-secondary` — white bg + slate border/text. Cancel-style actions.
 *  - `blue`              — solid #2563EB / white. Used for the help-center CTA.
 *
 * Pass a Font Awesome icon class via `leadingIcon` to render an icon before
 * the label. Projected content is the label.
 */
@Component({
  selector: 'app-v2-button',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './v2-button.component.html',
  styleUrls: ['./v2-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2ButtonComponent {
  readonly variant = input<V2ButtonVariant>('primary');
  readonly size = input<V2ButtonSize>('md');
  readonly leadingIcon = input<string | null>(null);
  readonly disabled = input<boolean, unknown>(false, {transform: booleanAttribute});
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  // `booleanAttribute` so bare `<app-v2-button fullWidth>` works the same as
  // `[fullWidth]="true"` — without it, the bare attribute passes an empty
  // string that never triggers the host class.
  readonly fullWidth = input<boolean, unknown>(false, {transform: booleanAttribute});

  readonly clicked = output<MouseEvent>();

  readonly buttonClasses = computed(() => {
    const classes = ['v2-btn', `v2-btn--${this.variant()}`, `v2-btn--${this.size()}`];
    if (this.fullWidth()) {
      classes.push('v2-btn--full');
    }
    return classes.join(' ');
  });

  /**
   * Reflects `fullWidth` as a host class so the component element itself
   * stretches to fill its parent. Without this the `:host` stays
   * `inline-block` and a `w-100` inside has no effect on siblings.
   */
  @HostBinding('class.v2-btn-host--full')
  get isFullWidthHost(): boolean {
    return this.fullWidth();
  }

  handleClick(event: MouseEvent): void {
    if (this.disabled()) {
      return;
    }
    this.clicked.emit(event);
  }
}
